// ============================================================================
// FOLLOW-UP OVERVIEW TAB
// Lean overview for item_type === 'follow_up'.
// Shows: parent context, description, due date / priority, and inline comments.
// Deliberately minimal — follow-ups are lightweight operational queries, not jobs.
// Date: March 2026
// ============================================================================

'use client';

import React from 'react';
import { Flag, HelpCircle, Calendar, ArrowUpRight, FileText } from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import CommentsSection from '../work-items/CommentsSection';

interface FollowUpOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, unknown>;
  onDataChange: (field: string, value: unknown) => void;
  isDark?: boolean;
  workItemId?: string;
  onOpenItem?: (itemId: string) => void;
  onCommentsCountChange?: (count: number) => void;
}

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent', 'critical'] as const;

// reminder_offsets: minutes BEFORE the due date (negative = overdue).
// Must mirror the options in RaiseFollowUpButton.
const REMINDER_OPTIONS = [
  { key: 'default', label: 'Day before, due date & 1 day overdue', offsets: null },
  { key: 'due_only', label: 'On the due date only', offsets: [0] },
  { key: 'before_only', label: 'Day before only', offsets: [1440] },
  { key: 'persistent', label: 'Day before, due date & daily while overdue', offsets: [1440, 0, -1440, -2880, -4320, -5760, -7200] },
  { key: 'none', label: 'No reminders', offsets: [] },
] as const;

function resolveReminderKey(value: unknown): (typeof REMINDER_OPTIONS)[number]['key'] {
  if (value === undefined || value === null) return 'default';
  if (!Array.isArray(value)) return 'default';
  const normalized = value.map(Number);
  const match = REMINDER_OPTIONS.find(
    (option) => Array.isArray(option.offsets)
      && option.offsets.length === normalized.length
      && option.offsets.every((offset, index) => offset === normalized[index]),
  );
  return match?.key ?? 'default';
}

export default function FollowUpOverviewTab({
  workItem,
  formData,
  onDataChange,
  isDark = false,
  workItemId,
  onOpenItem,
  onCommentsCountChange,
}: FollowUpOverviewTabProps) {
  const isQuery = workItem.item_subtype === 'query';
  const TypeIcon = isQuery ? HelpCircle : Flag;

  const description = typeof formData.description === 'string'
    ? formData.description
    : (workItem.description || '');
  const dueDate = typeof formData.due_date === 'string'
    ? formData.due_date
    : (workItem.due_date || '');
  const priority = typeof formData.priority === 'string'
    ? formData.priority
    : (workItem.priority || 'medium');
  const formCustomFields = (formData.custom_fields && typeof formData.custom_fields === 'object')
    ? formData.custom_fields as Record<string, unknown>
    : undefined;
  const reminderKey = resolveReminderKey(
    formCustomFields?.reminder_offsets ?? workItem.custom_fields?.reminder_offsets,
  );

  const handleReminderChange = (key: (typeof REMINDER_OPTIONS)[number]['key']) => {
    const offsets = REMINDER_OPTIONS.find((option) => option.key === key)?.offsets ?? null;
    onDataChange('custom_fields', {
      ...(formCustomFields ?? workItem.custom_fields ?? {}),
      reminder_offsets: offsets,
    });
  };

  const parentLabel = workItem.parent_job_number
    ? `${workItem.parent_job_number}${workItem.parent_job_title ? ` — ${workItem.parent_job_title}` : ''}`
    : null;

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 ${isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = `mb-1 block text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className="space-y-6">
      {/* Type + parent context */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${isDark ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-700'}`}>
          <TypeIcon className="h-3.5 w-3.5" />
          {isQuery ? 'Query' : 'Follow-Up'}
        </span>
        {workItem.item_number && (
          <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {workItem.item_number}
          </span>
        )}
        {workItem.parent_id && (
          <button
            type="button"
            onClick={() => onOpenItem?.(String(workItem.parent_id))}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            title="Open parent work item"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
            {parentLabel ? `Raised against ${parentLabel}` : 'Raised against parent item'}
          </button>
        )}
      </div>

      {/* Basic fields */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass}>
            <Calendar className="mr-1 inline h-3.5 w-3.5" />
            Due Date
          </label>
          <input
            type="date"
            value={dueDate ? String(dueDate).split('T')[0] : ''}
            onChange={(event) => onDataChange('due_date', event.target.value || null)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Priority</label>
          <select
            value={priority}
            onChange={(event) => onDataChange('priority', event.target.value)}
            className={inputClass}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Email Reminders</label>
          <select
            value={reminderKey}
            onChange={(event) => handleReminderChange(event.target.value as typeof reminderKey)}
            className={inputClass}
          >
            {REMINDER_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
          <p className={`mt-1 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Sent to the assignee and watchers of this follow-up.
          </p>
        </div>
      </div>

      {/* Description / notes */}
      <div>
        <label className={labelClass}>
          <FileText className="mr-1 inline h-3.5 w-3.5" />
          {isQuery ? 'Query Details' : 'Notes'}
        </label>
        <textarea
          value={description}
          onChange={(event) => onDataChange('description', event.target.value)}
          placeholder={isQuery ? 'What needs to be answered?' : 'Follow-up details...'}
          rows={3}
          className={`${inputClass} resize-y`}
        />
      </div>

      {/* Comments — first-class on the overview, not buried under Supporting Documents */}
      <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <CommentsSection
          workItemId={workItemId || workItem.id}
          isActive
          eagerLoad
          composerPresentation="inline"
          onCommentsChange={onCommentsCountChange}
        />
      </div>
    </div>
  );
}
